/**
 * Agent Registry
 *
 * Tracks all running agent instances with status, cancellation, and progress support.
 *
 * Responsibilities:
 * - Create tasks with unique IDs
 * - Track status: pending → running → completed/failed/cancelled
 * - Provide cancellation tokens for cooperative cancellation
 * - Report progress and stream chunks
 * - Clean up old/completed tasks
 */
import { generateTaskId } from "./idGenerator";
import { ThreadManager } from './threadManager';
// ═══════════════════════════════════════════════════════════════════════════
// CANCELLATION TOKEN IMPLEMENTATION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Create a cancellation token for cooperative cancellation
 */
function createCancellationToken() {
    let cancelled = false;
    return {
        get isCancelled() {
            return cancelled;
        },
        cancel() {
            cancelled = true;
        },
        throwIfCancelled() {
            if (cancelled) {
                throw new Error('Task was cancelled');
            }
        },
    };
}
// ═══════════════════════════════════════════════════════════════════════════
// AGENT REGISTRY CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Registry for tracking agent tasks
 *
 * @example
 * const registry = AgentRegistry.getInstance();
 *
 * // Create a task
 * const { taskId, threadId, cancellationToken } = registry.createTask({
 *     agentType: 'alphasense',
 *     agentName: 'alphaSenseSearchAgent',
 *     conversationId: 'conv_abc123',
 *     documentId: 'deck_xyz789',
 * });
 *
 * // Update status
 * registry.startTask(taskId);
 * registry.reportProgress(taskId, 50, 'Searching...');
 *
 * // Complete or fail
 * registry.completeTask(taskId, result);
 * // or
 * registry.failTask(taskId, 'Search failed');
 */
export class AgentRegistry {
    /**
     * Constructor - can be called directly for per-conversation registries
     */
    constructor() {
        /** All tracked tasks by ID */
        this.tasks = new Map();
        /** Cancellation tokens by task ID */
        this.cancellationTokens = new Map();
        /** Callbacks by task ID */
        this.callbacks = new Map();
        /** Agent instances registered in this registry (for per-conversation agent storage) */
        this.agents = new Map();
        // Create thread manager for this registry
        this._threadManager = new ThreadManager();
        // Start periodic cleanup
        this.startCleanupInterval();
    }
    /**
     * Get the thread manager for this registry
     * Used by agents to manage their conversation threads
     */
    get threadManager() {
        return this._threadManager;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // AGENT INSTANCE MANAGEMENT (for per-conversation agent storage)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Register an agent instance by name
     * Used for storing conversation-scoped agent instances (e.g., orchestrator)
     * If the agent has a setParentRegistry method, it will be called to link the agent to this registry.
     */
    registerAgent(name, agent) {
        this.agents.set(name, agent);
        // If agent has setParentRegistry, link it to this registry
        // This enables the agent to use this registry's ThreadManager
        if (agent && typeof agent.setParentRegistry === 'function') {
            agent.setParentRegistry(this);
            console.log(`[AgentRegistry] Registered agent: ${name} (with parent registry link)`);
        }
        else {
            console.log(`[AgentRegistry] Registered agent: ${name}`);
        }
    }
    /**
     * Get an agent instance by name
     */
    getAgent(name) {
        return this.agents.get(name);
    }
    /**
     * Check if an agent is registered
     */
    hasAgent(name) {
        return this.agents.has(name);
    }
    /**
     * Remove an agent instance
     */
    removeAgent(name) {
        this.agents.delete(name);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TASK LIFECYCLE
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Create a new task
     *
     * @param params - Task creation parameters
     * @returns Task ID, thread ID, and cancellation token
     */
    createTask(params) {
        const taskId = generateTaskId();
        // Create or get existing thread in this registry's ThreadManager
        const threadId = this._threadManager.getOrCreateThread({
            agentType: params.agentType,
            conversationId: params.conversationId,
            documentId: params.documentId,
            documentType: params.documentType,
            existingThreadId: params.threadId,
        });
        const cancellationToken = createCancellationToken();
        const task = {
            taskId,
            agentType: params.agentType,
            agentName: params.agentName,
            status: 'pending',
            threadId,
            conversationId: params.conversationId,
            documentId: params.documentId,
            documentType: params.documentType || 'unknown',
            createdAt: Date.now(),
        };
        this.tasks.set(taskId, task);
        this.cancellationTokens.set(taskId, cancellationToken);
        console.log(`[AgentRegistry] Created task ${taskId} for ${params.agentName} (thread: ${threadId})`);
        return { taskId, threadId, cancellationToken };
    }
    /**
     * Start a task (transition from pending to running)
     */
    updateStatus(taskId) {
        const task = this.tasks.get(taskId);
        if (!task) {
            console.warn(`[AgentRegistry] Task ${taskId} not found`);
            return;
        }
        if (task.status !== 'pending') {
            console.warn(`[AgentRegistry] Task ${taskId} is not pending (status: ${task.status})`);
            return;
        }
        task.status = 'running';
        task.startedAt = Date.now();
        console.log(`[AgentRegistry] Started task ${taskId}`);
    }
    /**
     * Complete a task successfully
     */
    completeTask(taskId, result) {
        const task = this.tasks.get(taskId);
        if (!task) {
            console.warn(`[AgentRegistry] Task ${taskId} not found`);
            return;
        }
        task.status = 'completed';
        task.completedAt = Date.now();
        task.result = result;
        task.progress = 100;
        console.log(`[AgentRegistry] Completed task ${taskId}`);
    }
    /**
     * Fail a task with an error
     */
    failTask(taskId, error) {
        const task = this.tasks.get(taskId);
        if (!task) {
            console.warn(`[AgentRegistry] Task ${taskId} not found`);
            return;
        }
        task.status = 'failed';
        task.completedAt = Date.now();
        task.error = error;
        console.log(`[AgentRegistry] Failed task ${taskId}: ${error}`);
    }
    /**
     * Cancel a task
     * Sets the cancellation token so the task can check and stop gracefully
     */
    cancelTask(taskId) {
        const task = this.tasks.get(taskId);
        if (!task) {
            console.warn(`[AgentRegistry] Task ${taskId} not found`);
            return;
        }
        if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
            console.warn(`[AgentRegistry] Task ${taskId} already finished (status: ${task.status})`);
            return;
        }
        // Set cancellation token
        const token = this.cancellationTokens.get(taskId);
        if (token) {
            token.cancel();
        }
        task.status = 'cancelled';
        task.completedAt = Date.now();
        console.log(`[AgentRegistry] Cancelled task ${taskId}`);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // PROGRESS & CALLBACKS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Register callbacks for a task
     */
    registerCallbacks(taskId, callbacks) {
        this.callbacks.set(taskId, callbacks);
    }
    /**
     * Report progress on a task
     */
    reportProgress(taskId, percent, message) {
        const task = this.tasks.get(taskId);
        if (!task)
            return;
        task.progress = Math.min(100, Math.max(0, percent));
        if (message) {
            task.progressMessage = message;
        }
        // Notify callback
        const callbacks = this.callbacks.get(taskId);
        callbacks?.onProgress?.(task.progress, message);
    }
    /**
     * Report a text chunk (for streaming)
     */
    reportChunk(taskId, chunk) {
        const callbacks = this.callbacks.get(taskId);
        callbacks?.onChunk?.(chunk);
    }
    /**
     * Report a tool call
     */
    reportToolCall(taskId, toolName, args) {
        const callbacks = this.callbacks.get(taskId);
        callbacks?.onToolCall?.(toolName, args);
    }
    /**
     * Report a tool result
     */
    reportToolResult(taskId, toolName, result) {
        const callbacks = this.callbacks.get(taskId);
        callbacks?.onToolResult?.(toolName, result);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // QUERIES
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get a task by ID
     */
    getTask(taskId) {
        return this.tasks.get(taskId);
    }
    /**
     * Get the cancellation token for a task
     */
    getCancellationToken(taskId) {
        return this.cancellationTokens.get(taskId);
    }
    /**
     * Get all tasks with a specific status
     */
    getTasksByStatus(status) {
        return Array.from(this.tasks.values()).filter(task => task.status === status);
    }
    /**
     * Get all running tasks
     */
    getRunningTasks() {
        return this.getTasksByStatus('running');
    }
    /**
     * Get all tasks for a conversation
     */
    getTasksForConversation(conversationId) {
        return Array.from(this.tasks.values()).filter(task => task.conversationId === conversationId);
    }
    /**
     * Get all tasks for a deck
     */
    getTasksForDeck(documentId) {
        return Array.from(this.tasks.values()).filter(task => task.documentId === documentId);
    }
    /**
     * Check if a task is still running
     */
    isTaskRunning(taskId) {
        const task = this.tasks.get(taskId);
        return task?.status === 'running';
    }
    /**
     * Check if a task was cancelled
     */
    isTaskCancelled(taskId) {
        const task = this.tasks.get(taskId);
        return task?.status === 'cancelled';
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Remove a task from the registry
     */
    removeTask(taskId) {
        this.tasks.delete(taskId);
        this.cancellationTokens.delete(taskId);
        this.callbacks.delete(taskId);
    }
    /**
     * Clean up old completed tasks
     */
    cleanupOldTasks() {
        const now = Date.now();
        const toRemove = [];
        Array.from(this.tasks.entries()).forEach(([taskId, task]) => {
            // Remove completed/failed/cancelled tasks older than TTL
            if ((task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') &&
                task.completedAt &&
                now - task.completedAt > AgentRegistry.COMPLETED_TASK_TTL) {
                toRemove.push(taskId);
            }
        });
        for (const taskId of toRemove) {
            this.removeTask(taskId);
        }
        if (toRemove.length > 0) {
            console.log(`[AgentRegistry] Cleaned up ${toRemove.length} old tasks`);
        }
    }
    /**
     * Start periodic cleanup interval
     */
    startCleanupInterval() {
        this.cleanupIntervalId = setInterval(() => {
            this.cleanupOldTasks();
        }, AgentRegistry.CLEANUP_INTERVAL);
    }
    /**
     * Clear all tasks (for testing)
     */
    clearAll() {
        this.tasks.clear();
        this.cancellationTokens.clear();
        this.callbacks.clear();
    }
    /**
     * Dispose of this registry - clears cleanup interval and all data
     * Call when conversation is closed to prevent memory leaks
     */
    dispose() {
        // Clear the cleanup interval
        if (this.cleanupIntervalId) {
            clearInterval(this.cleanupIntervalId);
            this.cleanupIntervalId = undefined;
        }
        // Clear all data
        this.tasks.clear();
        this.cancellationTokens.clear();
        this.callbacks.clear();
        this.agents.clear();
        console.log('[AgentRegistry] Disposed');
    }
}
/** Task cleanup interval (ms) */
AgentRegistry.CLEANUP_INTERVAL = 60 * 60 * 1000; // 1 hour
/** How long to keep completed tasks (ms) */
AgentRegistry.COMPLETED_TASK_TTL = 30 * 60 * 1000; // 30 minutes
